import { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import { MapPin, Phone, CheckCircle, Calendar } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function PanditProfilePage() {
  const { panditId } = useParams();
  const [pandit, setPandit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showBooking, setShowBooking] = useState(false);
  const [bookingData, setBookingData] = useState({
    name: '',
    phone: '',
    date: '',
    puja_type: '',
    message: '',
  });

  useEffect(() => {
    fetchPandit();
  }, [panditId]);

  const fetchPandit = async () => {
    try {
      const response = await axios.get(`${API}/pandit/profile/${panditId}`);
      setPandit(response.data);
    } catch (error) {
      toast.error('Failed to load pandit profile');
    } finally {
      setLoading(false);
    }
  };

  const handleBooking = (e) => {
    e.preventDefault();
    toast.success('Booking request sent! Pandit Ji will contact you soon.');
    setShowBooking(false);
    setBookingData({ name: '', phone: '', date: '', puja_type: '', message: '' });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="text-4xl text-[#FF9933] mb-2">ॐ</div>
          <p className="text-slate-600">Loading profile...</p>
        </div>
      </div>
    );
  }

  if (!pandit) {
    return (
      <div className="text-center py-12">
        <p className="text-slate-600">Pandit not found</p>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 py-6">
      <div className="bg-white rounded-2xl shadow-sm border border-stone-100 p-8 peaceful-enter">
        {/* Header */}
        <div className="flex flex-col md:flex-row gap-6 items-center md:items-start mb-8 pb-8 border-b border-stone-100">
          <img
            src={pandit.avatar || 'https://images.unsplash.com/photo-1762838105804-c3264c29a390?w=200&h=200&fit=crop'}
            alt={pandit.name}
            className="w-32 h-32 rounded-full object-cover border-4 border-orange-100"
          />

          <div className="flex-1 text-center md:text-left">
            <div className="flex items-center gap-2 justify-center md:justify-start mb-2">
              <h1 className="text-3xl font-bold text-slate-800" style={{ fontFamily: 'Merriweather, serif' }}>
                {pandit.name}
              </h1>
              {pandit.verified && (
                <CheckCircle className="w-6 h-6 text-[#FF9933]" fill="#FF9933" />
              )}
            </div>

            <div className="flex flex-wrap gap-4 text-slate-600 mb-4 justify-center md:justify-start">
              <div className="flex items-center gap-1">
                <MapPin className="w-4 h-4" />
                <span>{pandit.location}</span>
              </div>
              <div className="flex items-center gap-1">
                <Phone className="w-4 h-4" />
                <span>{pandit.phone}</span>
              </div>
            </div>

            <p className="text-2xl font-semibold text-[#FF9933] mb-4">
              ₹{pandit.fees_per_puja} per puja
            </p>

            <Button
              data-testid="book-pandit-btn"
              onClick={() => setShowBooking(true)}
              className="rounded-full bg-[#FF9933] hover:bg-[#E68A00] px-8 py-5 font-semibold shadow-md hover:-translate-y-0.5 transition-all"
            >
              <Calendar className="w-5 h-5 mr-2" />
              Book Puja
            </Button>
          </div>
        </div>

        {/* Details */}
        <div className="space-y-6">
          <div>
            <h3 className="text-xl font-semibold text-slate-800 mb-3" style={{ fontFamily: 'Merriweather, serif' }}>
              Experience
            </h3>
            <p className="text-slate-600">{pandit.experience_years} years of spiritual service</p>
          </div>

          <div>
            <h3 className="text-xl font-semibold text-slate-800 mb-3" style={{ fontFamily: 'Merriweather, serif' }}>
              Puja Types
            </h3>
            <div className="flex flex-wrap gap-2">
              {pandit.puja_types.map((puja, i) => (
                <span key={i} className="px-4 py-2 bg-orange-50 text-orange-700 rounded-full font-medium">
                  {puja}
                </span>
              ))}
            </div>
          </div>

          <div>
            <h3 className="text-xl font-semibold text-slate-800 mb-3" style={{ fontFamily: 'Merriweather, serif' }}>
              Languages
            </h3>
            <div className="flex flex-wrap gap-2">
              {pandit.languages.map((lang, i) => (
                <span key={i} className="px-4 py-2 bg-slate-100 text-slate-700 rounded-full">
                  {lang}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Booking Dialog */}
      <Dialog open={showBooking} onOpenChange={setShowBooking}>
        <DialogContent className="rounded-2xl">
          <DialogHeader>
            <DialogTitle>Book Puja with {pandit.name}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleBooking} className="space-y-4 mt-4">
            <div>
              <Label htmlFor="name">Your Name</Label>
              <Input
                id="name"
                data-testid="booking-name-input"
                value={bookingData.name}
                onChange={(e) => setBookingData({ ...bookingData, name: e.target.value })}
                required
                className="rounded-xl border-stone-200"
              />
            </div>
            <div>
              <Label htmlFor="phone">Your Phone</Label>
              <Input
                id="phone"
                data-testid="booking-phone-input"
                value={bookingData.phone}
                onChange={(e) => setBookingData({ ...bookingData, phone: e.target.value })}
                required
                className="rounded-xl border-stone-200"
              />
            </div>
            <div>
              <Label htmlFor="date">Preferred Date</Label>
              <Input
                id="date"
                data-testid="booking-date-input"
                type="date"
                value={bookingData.date}
                onChange={(e) => setBookingData({ ...bookingData, date: e.target.value })}
                required
                className="rounded-xl border-stone-200"
              />
            </div>
            <div>
              <Label htmlFor="puja_type">Puja Type</Label>
              <Input
                id="puja_type"
                data-testid="booking-puja-input"
                placeholder="e.g., Ganesh Puja"
                value={bookingData.puja_type}
                onChange={(e) => setBookingData({ ...bookingData, puja_type: e.target.value })}
                required
                className="rounded-xl border-stone-200"
              />
            </div>
            <div>
              <Label htmlFor="message">Additional Message</Label>
              <Textarea
                id="message"
                data-testid="booking-message-input"
                value={bookingData.message}
                onChange={(e) => setBookingData({ ...bookingData, message: e.target.value })}
                rows={3}
                className="rounded-xl border-stone-200"
              />
            </div>
            <Button
              type="submit"
              data-testid="submit-booking-btn"
              className="w-full rounded-full bg-[#FF9933] hover:bg-[#E68A00] py-5"
            >
              Send Booking Request
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
